import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { TransitionLink } from '@/components/generals/transition-link'
import { ServiceAreaMap } from '@/components/widgets/service-area-map'
import { serviceAreas } from '@/constants/areas'

interface AreaListProps {
    activeSlug: string | null
    onActivate: (slug: string | null) => void
    showArrival?: boolean
    className?: string
}

export const AreaList: FC<AreaListProps> = ({ activeSlug, onActivate, showArrival = true, className }): ReactNode => {
    return (
        <ul className={cn('grid gap-px overflow-hidden rounded-[24px] border border-cream/10 bg-cream/10 sm:grid-cols-2', className)}>
            {serviceAreas.map(area => (
                <li key={area.slug}>
                    <TransitionLink
                        href={`/service-areas/${area.slug}`}
                        onMouseEnter={() => onActivate(area.slug)}
                        onMouseLeave={() => onActivate(null)}
                        onFocus={() => onActivate(area.slug)}
                        onBlur={() => onActivate(null)}
                        className={cn(
                            'group flex h-full items-center justify-between gap-4 bg-ink px-5 py-4 transition-colors duration-300',
                            activeSlug === area.slug ? 'bg-graphite' : 'hover:bg-graphite'
                        )}
                    >
                        <span>
                            <span className='block font-medium text-cream'>{area.name}</span>

                            <span className='mt-0.5 block font-mono text-[11.5px] text-cream/45'>
                                {area.zips.join(', ')}
                                {showArrival && ` · ${area.arrival}`}
                            </span>
                        </span>

                        <ArrowUpRight
                            className={cn(
                                'size-4 shrink-0 transition-all duration-300',
                                activeSlug === area.slug ? 'text-ember-soft opacity-100' : 'text-cream/30 group-hover:text-ember-soft'
                            )}
                            aria-hidden='true'
                        />
                    </TransitionLink>
                </li>
            ))}
        </ul>
    )
}

interface AreaExplorerProps {
    className?: string
    children?: ReactNode
}

export const AreaExplorer: FC<AreaExplorerProps> = ({ className, children }): ReactNode => {
    const [activeSlug, setActiveSlug] = useState<string | null>(null)

    return (
        <div className={cn('grid gap-12 lg:grid-cols-12 lg:items-center lg:gap-10', className)}>
            <div className='lg:col-span-5'>
                {children}

                <AreaList activeSlug={activeSlug} onActivate={setActiveSlug} className='mt-10' />
            </div>

            <div className='lg:col-span-7'>
                <div className='relative mx-auto max-w-[620px] rounded-[32px] border border-cream/10 bg-cream/[0.03] p-4 text-cream sm:p-8'>
                    <ServiceAreaMap activeSlug={activeSlug} onActivate={setActiveSlug} />

                    <p className='mt-2 text-center font-mono text-[10.5px] uppercase tracking-[0.18em] text-cream/40'>
                        Usual arrival on urgent calls, leaving the Riverton yard
                    </p>
                </div>
            </div>
        </div>
    )
}
